// Factory for Google Maps Advanced Markers used on the Events map page.
//
// Advanced Markers are imperative (created on the map instance, not as React
// children), so this module builds the marker's custom HTML content and wires
// the hover "N going" badge + click-to-detail behavior. Keeping it separate
// keeps EventsMapPage focused on map/data orchestration.

import { isExternalEvent } from '../../utils/externalEvents';

function goingLabel(count) {
  const n = Number.isFinite(count) ? count : 0;
  return n === 1 ? '1 going' : `${n} going`;
}

/**
 * Badge text for a pin. External events have no CivixThread RSVPs, so a going
 * count would always read "0 going" — name the host instead.
 */
function markerBadgeLabel(event) {
  if (!isExternalEvent(event)) return goingLabel(event.going_count);
  return event.external_organization_name || 'Hosted on Mobilize';
}

/** Build the marker's DOM: a teardrop location-pin with a hover badge. */
export function buildEventMarkerContent(event) {
  const wrapper = document.createElement('div');
  wrapper.className = 'event-map-marker';

  const badge = document.createElement('div');
  badge.className = 'event-map-marker__badge';
  badge.textContent = markerBadgeLabel(event);

  const image = event.cover_image || event.cover_image_url || '';

  // Build an SVG location pin (teardrop) so the shape scales cleanly at all
  // DPRs and the cover image is clipped into the pin head via clipPath.
  const svgNS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(svgNS, 'svg');
  svg.setAttribute('width', '30');
  svg.setAttribute('height', '42');
  svg.setAttribute('viewBox', '0 0 30 42');
  svg.classList.add('event-map-marker__svg');

  // Subtle ground shadow beneath the pin tip.
  const shadow = document.createElementNS(svgNS, 'ellipse');
  shadow.setAttribute('cx', '15');
  shadow.setAttribute('cy', '40');
  shadow.setAttribute('rx', '5');
  shadow.setAttribute('ry', '2');
  shadow.setAttribute('fill', 'rgba(15,23,42,0.15)');
  svg.appendChild(shadow);

  // Classic teardrop pin body — circle head tapering to a point at (15, 42).
  const body = document.createElementNS(svgNS, 'path');
  body.setAttribute(
    'd',
    'M15,0 C6.7,0 0,6.7 0,15 C0,26.25 15,42 15,42 C15,42 30,26.25 30,15 C30,6.7 23.3,0 15,0 Z',
  );
  body.setAttribute('fill', '#ffffff');
  body.setAttribute('stroke', '#0f172a');
  body.setAttribute('stroke-width', '2');
  svg.appendChild(body);

  if (image) {
    const defs = document.createElementNS(svgNS, 'defs');
    const clipPath = document.createElementNS(svgNS, 'clipPath');
    // Unique ID so multiple markers on the same page don't share a clip.
    const clipId = `ecm-${Math.random().toString(36).slice(2, 9)}`;
    clipPath.setAttribute('id', clipId);
    const clipCircle = document.createElementNS(svgNS, 'circle');
    clipCircle.setAttribute('cx', '15');
    clipCircle.setAttribute('cy', '15');
    clipCircle.setAttribute('r', '12');
    clipPath.appendChild(clipCircle);
    defs.appendChild(clipPath);
    svg.appendChild(defs);

    const img = document.createElementNS(svgNS, 'image');
    img.setAttribute('href', image.replace(/"/g, '%22'));
    img.setAttribute('x', '3');
    img.setAttribute('y', '3');
    img.setAttribute('width', '24');
    img.setAttribute('height', '24');
    img.setAttribute('clip-path', `url(#${clipId})`);
    img.setAttribute('preserveAspectRatio', 'xMidYMid slice');
    svg.appendChild(img);
  } else {
    // White inner dot — classic hollow-pin look when there is no cover image.
    const dot = document.createElementNS(svgNS, 'circle');
    dot.setAttribute('cx', '15');
    dot.setAttribute('cy', '15');
    dot.setAttribute('r', '5');
    dot.setAttribute('fill', '#0f172a');
    dot.setAttribute('opacity', '0.8');
    svg.appendChild(dot);
  }

  wrapper.appendChild(badge);
  wrapper.appendChild(svg);
  return wrapper;
}

/**
 * Create an AdvancedMarkerElement for an event and add it to the map.
 * Returns the marker (call ``marker.map = null`` to remove it) or null when the
 * event has no coordinates or the marker library is unavailable.
 */
export function createEventMarker({ map, event, onSelect }) {
  const AdvancedMarkerElement = window.google?.maps?.marker?.AdvancedMarkerElement;
  if (!AdvancedMarkerElement) return null;

  const lat = typeof event.latitude === 'number' ? event.latitude : parseFloat(event.latitude);
  const lng = typeof event.longitude === 'number' ? event.longitude : parseFloat(event.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  const content = buildEventMarkerContent(event);
  const marker = new AdvancedMarkerElement({
    map,
    position: { lat, lng },
    content,
    // Makes the pin reachable by Tab and activatable by Enter, and exposes the
    // title as its accessible name.
    gmpClickable: true,
    title: `${markerBadgeLabel(event)} — ${event.title || 'Event'}`,
  });

  if (typeof onSelect === 'function') {
    marker.addListener('gmp-click', () => onSelect(event));
  }

  return marker;
}
